import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type FieldHelpProps = {
  description: string;
  example?: string;
};

type PanelPosition = {
  top: number;
  left: number;
};

const PANEL_WIDTH = 288;
const VIEWPORT_GAP = 8;

export function FieldHelp({ description, example }: FieldHelpProps) {
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition>({ top: 0, left: 0 });

  function updatePosition(): void {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    const panelHeight = panelRef.current?.offsetHeight ?? 160;
    const maxLeft = window.innerWidth - PANEL_WIDTH - VIEWPORT_GAP;
    const left = Math.max(VIEWPORT_GAP, Math.min(rect.left, maxLeft));
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_GAP;
    const top =
      spaceBelow < panelHeight && rect.top > panelHeight + VIEWPORT_GAP
        ? rect.top - panelHeight - VIEWPORT_GAP
        : rect.bottom + VIEWPORT_GAP;

    setPosition({ top, left });
  }

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent): void {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    function handleViewportChange(): void {
      updatePosition();
    }

    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`inline-flex shrink-0 rounded-full p-0.5 transition-colors ${
          open
            ? 'text-primary-600'
            : 'text-secondary-400 hover:bg-primary-50 hover:text-primary-600'
        }`}
        aria-label="Ajuda sobre este campo"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <QuestionMarkCircleIcon className="h-4 w-4" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="note"
              className="fixed z-[1100] w-72 rounded-xl border border-secondary-200 bg-white p-3 text-left shadow-lg"
              style={{ top: position.top, left: position.left }}
            >
              <p className="text-sm text-secondary-700">{description}</p>
              {example ? (
                <p className="mt-2 rounded-lg bg-secondary-50 px-2.5 py-2 text-xs text-secondary-600">
                  <span className="font-semibold text-secondary-800">Exemplo: </span>
                  {example}
                </p>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
